import { createSelector } from "reselect"
import { get, forEach, find, capitalize, toUpper } from "lodash"
import { isDefaultCurrency, makeCurrencyFormatter, percentageFormatter } from "helpers/formatters/numbers"
import { getAllAvailableRollups, getSelectedGroup as getRollupSelectedGroup } from "helpers/rollups"
import { getCurrency } from "."
import { VIEW_TYPES } from "helpers/base"
import { safeDivide } from "helpers/math"
import { POSITIONS_DISPLAY_TYPES } from "helpers/comparison"

const getRawSelectedMetric = state => get(state, "modules.compareComposition.selectedMetric")
const getRawSelectedGroup = state => get(state, "modules.compareComposition.selectedGroup")
const getRawPositionsDisplayType = state => get(state, "modules.compareComposition.positionsDisplayType")
const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getSavedMetric = (_, props) => get(props, "savedMetric")
const getSavedGroup = (_, props) => get(props, "savedGroup")
const getEndDate = (_, props) => get(props, "endDate")
const getViewType = (_, props) => get(props, "viewType")
const getIsActive = createSelector([getViewType], viewType => viewType === VIEW_TYPES.ACTIVE)

const defaultMetric = "gmv"
const defaultPositionsDisplayType = "all"

export const getMetrics = createSelector(
  [getCurrency, getIsActive],
  computeCompositionMetrics,
)

export const getGroups = createSelector(
  [getClassifications, getEndDate, getViewType],
  (classifications, date, viewType) =>
    getAllAvailableRollups({ classifications }, date, viewType),
)

export const getSelectedMetric = createSelector(
  [getMetrics, getRawSelectedMetric, getSavedMetric],
  (metrics, selectedMetric, savedMetric) => {
    const metric = selectedMetric || savedMetric || defaultMetric

    let metricData = null
    let defaultData = null
    forEach(metrics, item => {
      const data = find(item.options, { id: metric })
      defaultData = !defaultData && find(item.options, { id: defaultMetric })

      if (data) {
        metricData = data
        return false
      }
    })

    return metricData || defaultData
  },
)

export const getSelectedGroup = createSelector(
  [getGroups, getRawSelectedGroup, getSavedGroup],
  getRollupSelectedGroup,
)

export const getPositionsDisplayType = createSelector(
  [getRawPositionsDisplayType],
  (selectedPositionsDisplayType) => {
    const displayType = selectedPositionsDisplayType || defaultPositionsDisplayType
    let typeData = null

    forEach(POSITIONS_DISPLAY_TYPES, item => {
      const data = find(item.options, { id: displayType })

      if (data) {
        typeData = data
        return false
      }
    })

    return typeData
  },
)
export function computeCompositionMetrics(currency, isActive) {
  const currencyFormatter = makeCurrencyFormatter(currency)
  const currencyMargin = isDefaultCurrency(currency) ? 70 : 80
  const longId = isActive ? "overweight" : "long"
  const shortId = isActive ? "underweight" : "short"

  return [{
    id: "composition",
    label: "Composition",
    category: "composition",
    options: [{
      label: "GMV",
      id: "gmv",
      chartMargins: { left: currencyMargin },
      formatter: currencyFormatter,
      chartValue: item => get(item, "gmv"),
      value: "gmv",
      category: "composition",
    }, {
      label: "Reference Equity",
      id: "equity",
      chartMargins: { left: currencyMargin },
      formatter: currencyFormatter,
      chartValue: item => get(item, "referenceEquity"),
      value: "equity",
      category: "composition",
    }, {
      label: "Model Coverage",
      id: "modelCoverage",
      chartMargins: { left: 70 },
      formatter: data => percentageFormatter(data),
      chartValue: item => safeDivide(get(item, "modeledGmv"), get(item, "gmv")),
      value: "modelCoverage",
      category: "composition",
    }, {
      label: "Positions",
      id: "positions",
      chartMargins: { left: 40 },
      formatter: data => data,
      chartValue: item => get(item, "positionsCount"),
      value: "positions",
      category: "composition",
    }, {
      label: "Max Concentration",
      id: "maxConcentration",
      formatter: data => percentageFormatter(data, true),
      chartValue: item => get(item, "concentration.max"),
      value: "maxConcentration",
      category: "composition",
    }, {
      label: "Min Concentration",
      id: "minConcentration",
      formatter: data => percentageFormatter(data, true),
      chartValue: item => get(item, "concentration.min"),
      value: "minConcentration",
      category: "composition",
    }, {
      label: capitalize(longId),
      id: longId,
      formatter: data => percentageFormatter(data, true),
      chartValue: item => get(find(item.longShortComposition, { id: toUpper(longId) }), "economicExposure", 0),
      value: longId,
      category: "composition",
    }, {
      label: capitalize(shortId),
      id: shortId,
      formatter: data => percentageFormatter(data, true),
      chartValue: item => Math.abs(get(find(item.longShortComposition, { id: toUpper(shortId) }), "economicExposure", 0)),
      value: shortId,
      category: "composition",
    }, {
      label: "Net",
      id: "net",
      formatter: data => percentageFormatter(data, true),
      chartValue: item => {
        const long = get(find(item.longShortComposition, { id: toUpper(longId) }), "economicExposure", 0)
        const short = get(find(item.longShortComposition, { id: toUpper(shortId) }), "economicExposure", 0)
        return long - Math.abs(short)
      },
      value: "net",
      category: "composition",
    }],
  }]
}
